import {announce} from '@github-ui/aria-live'
import type {CodeNavigationInfo, DefinitionOrReference} from '@github-ui/code-nav'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import type {BlobLayoutStyledBlobPayload} from '@github-ui/code-view-types'
import {noop} from '@github-ui/noop'
import {useTaillightsController} from '@github-ui/taillights/provider'
import type {SourceDocument} from '@github-ui/taillights-worker/document'
import {useInFileSearchResults} from '@github-ui/use-in-file-search-results'
import {useTheme} from '@primer/react'
import {type ReactNode, use, useEffect, useMemo, useState} from 'react'

import {useCurrentStyledBlob} from '../../hooks/CurrentStyledBlob'
import {useShouldResolveTaillightsSource} from '../../hooks/use-should-use-taillights'
import {CodeBlobTaillightsProvider} from './CodeBlobTaillightsProvider'
import {TaillightsBlobContext} from './source-context'

// Stable empty reference so the derived symbol highlight keeps an identical identity when nothing is selected.
const EMPTY_SEARCH_RESULTS: DefinitionOrReference[] = []
const EMPTY_SOURCE_LINES: string[] = []

function useWorkerMatches(codeNavInfo: CodeNavigationInfo | undefined, query: string) {
  const controller = useTaillightsController()
  const request = useMemo(() => ({controller, codeNavInfo, query}), [controller, codeNavInfo, query])
  const [response, setResponse] = useState<{request: typeof request; results: DefinitionOrReference[]}>()
  const validQuery = query.length > 0 && query.length <= 1000
  useEffect(() => {
    if (!controller || !codeNavInfo || !validQuery) return
    let active = true
    void (async () => {
      try {
        const result = await controller.search({query, kind: 'symbol', index: 0})
        if (active) setResponse({request, results: codeNavInfo.createReferences([...result.highlights])})
      } catch (error) {
        if (!active || controller.disposed) return
        reportError(error)
        announce('Symbol highlighting failed. Select the symbol again to retry.')
        setResponse({request, results: []})
      }
    })()
    return () => {
      active = false
    }
  }, [controller, codeNavInfo, query, validQuery, request])
  return validQuery && response?.request === request ? response.results : EMPTY_SEARCH_RESULTS
}

type BlobSearchState = Pick<
  ReturnType<typeof useInFileSearchResults>,
  'searchResults' | 'focusedSearchResult' | 'setFocusedSearchResult' | 'searchStatus'
> & {
  overlaySearchResults: DefinitionOrReference[]
  overlayFocusedSearchResult: number | undefined
}

type SearchProps = {
  codeNavInfo: CodeNavigationInfo | undefined
  searchTerm: string
  selectedSymbol: string
  findInFileActive: boolean
  children: (search: BlobSearchState) => ReactNode
}

function BlobSearch({
  codeNavInfo,
  searchTerm,
  selectedSymbol,
  findInFileActive,
  children,
  taillights = false,
}: SearchProps & {taillights?: boolean}) {
  // Find-in-file matches come from the worker, keyed on the active search term.
  const search = useInFileSearchResults(codeNavInfo, searchTerm, !taillights)
  const workerSymbol = useWorkerMatches(taillights ? codeNavInfo : undefined, selectedSymbol)

  // The clicked-symbol highlight is a pure function of the current blob and the selected symbol, so we derive
  // it during render instead of pushing it up from the panel through an effect. `getReferencesToSymbol` returns
  // every in-file occurrence (definition + usages) — exactly the set the overlay highlights — synchronously, so
  // the clicked token is highlighted immediately and reliably regardless of how async code-nav defs/refs resolve.
  const symbol = useMemo(
    () =>
      !taillights && codeNavInfo && selectedSymbol
        ? codeNavInfo.getReferencesToSymbol(selectedSymbol)
        : EMPTY_SEARCH_RESULTS,
    [taillights, codeNavInfo, selectedSymbol],
  )
  if (taillights) {
    return children({
      searchResults: EMPTY_SEARCH_RESULTS,
      focusedSearchResult: undefined,
      setFocusedSearchResult: noop,
      searchStatus: 'done',
      overlaySearchResults: workerSymbol,
      overlayFocusedSearchResult: undefined,
    })
  }
  return children({
    ...search,
    // Find-in-file owns the highlight overlay while it is actively searching; otherwise the selected symbol does.
    overlaySearchResults: findInFileActive ? search.searchResults : symbol,
    overlayFocusedSearchResult: findInFileActive ? search.focusedSearchResult : undefined,
  })
}

export function BlobSearchProvider({
  taillights,
  tabSize,
  ...props
}: SearchProps & {taillights: boolean; tabSize: number}) {
  const streamedSource = useShouldResolveTaillightsSource()
  if (!taillights && !streamedSource) return <BlobSearch {...props} />
  return (
    <TaillightsBlobSearchProvider
      {...props}
      tabSize={tabSize}
      taillights={taillights}
      streamedSource={streamedSource}
    />
  )
}

export function useBlobSourceDocument(
  blob: BlobLayoutStyledBlobPayload | null,
  codeSections?: CodeNavigationInfo['codeSections'],
) {
  const path = usePath()
  const rawLines = blob?.rawLines
  const stylingDirectives = blob?.stylingDirectives
  return useMemo<SourceDocument>(
    () => ({
      id: path,
      // Match the existing Code View rows so textarea newline normalization cannot shift source offsets.
      lines: rawLines?.map(line => line.replace(/[\n\r]/g, '')) ?? EMPTY_SOURCE_LINES,
      directives: stylingDirectives,
      sections: codeSections ? [...new Set([...codeSections.values()].flat())] : [],
    }),
    [path, rawLines, stylingDirectives, codeSections],
  )
}

function TaillightsBlobSearchProvider({
  tabSize,
  taillights,
  streamedSource,
  ...props
}: SearchProps & {tabSize: number; taillights: boolean; streamedSource: boolean}) {
  const blob = useCurrentStyledBlob()
  const source = useBlobSourceDocument(
    taillights ? blob : null,
    taillights ? props.codeNavInfo?.codeSections : undefined,
  )
  const {colorScheme} = useTheme()
  const content = (
    <CodeBlobTaillightsProvider source={taillights ? source : null} tabSize={tabSize} appearanceKey={colorScheme}>
      <BlobSearch {...props} taillights={taillights} />
    </CodeBlobTaillightsProvider>
  )
  return streamedSource ? content : <TaillightsBlobContext value>{content}</TaillightsBlobContext>
}

export function useTaillightsBlobEnabled() {
  return use(TaillightsBlobContext)
}
